import cx from 'classnames'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState, type FC } from 'react'
import { EmptyState, Spinner } from '~/elements'
import {
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Input,
  Button,
  Label,
  showErrorToast,
} from '@bpinternal/ui-kit-next'
import { cn } from '~/utils'
import Terminal from '~/assets/emptyStates/terminal.svg?react'
import { InView } from 'react-intersection-observer'
import { ChevronDown, ChevronUp, Filter, RefreshCw } from 'lucide-react'
import { FiltersPopover } from './FiltersPopover'
import { useDebounceEffect, useLogs } from '~/hooks'
import { escapeForRegEx } from '@tiptap/core'
import { PageActionPortal } from '~/components'
import { DateInput } from './DateInput'

type LogsPanelProps = {
  id: string
  type: 'bot' | 'integration'
  workspaceId: string
  startDate: Date
  endDate: Date
  setStartDate: (date: Date) => void
  setEndDate: (date: Date) => void
  conversationId?: string
  userId?: string
  level?: 'INFO' | 'ERROR' | 'WARN' | 'DEBUG'
  messageContains?: string
  setMessageContains: (message: string | undefined) => void
}

export const LogsPanel: FC<LogsPanelProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  conversationId,
  userId,
  level,
  id,
  type,
  workspaceId,
  messageContains,
  setMessageContains,
}) => {
  const topRef = useRef<null | HTMLDivElement>(null)
  const bottomRef = useRef<null | HTMLDivElement>(null)

  const [isAtTop, setIsAtTop] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true)
  const [rawMessageContains, setRawMessageContains] = useState(messageContains)
  const [dateRange, setDateRange] = useState({ startDate, endDate })

  const handleStartDateChange = (date: Date) => {
    if (date > dateRange.endDate) {
      showErrorToast('Start date cannot be after end date')
      return
    }
    setDateRange((prev) => ({ ...prev, startDate: date }))
    setStartDate(date)
  }

  const handleEndDateChange = (date: Date) => {
    if (date < dateRange.startDate) {
      showErrorToast('End date cannot be before start date')
      return
    }
    setDateRange((prev) => ({ ...prev, endDate: date }))
    setEndDate(date)
  }

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'instant' })
  }

  const scrollToTop = () => {
    topRef.current?.scrollIntoView({ behavior: 'instant' })
  }

  const { data, refetch, isFetching, hasPreviousPage, fetchPreviousPage, isFetchingPreviousPage } = useLogs({
    type,
    id,
    workspaceId,
    startDate,
    endDate: endDate ?? new Date(),
    conversationId,
    level,
    userId,
    messageContains,
  })

  const logs = data?.pages.flatMap((p) => p.logs) ?? []

  // refresh when r key is pressed
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'r' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        refetch()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [refetch])

  useEffect(() => {
    if (shouldScrollToBottom && !isFetching) {
      scrollToBottom()
      setShouldScrollToBottom(false)
    }
  }, [shouldScrollToBottom, isFetching])

  useDebounceEffect(
    () => {
      setShouldScrollToBottom(true)
      setMessageContains(rawMessageContains)
    },
    [rawMessageContains],
    500
  )

  return (
    <div className="flex h-full flex-col gap-px overflow-hidden">
      <PageActionPortal>
        <div className="flex gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="messageContains">Filter</Label>
              <Input
                className="shadow-none"
                id="messageContains"
                value={rawMessageContains}
                onChange={(event) => setRawMessageContains(event.target.value)}
                placeholder="Log message content"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>From</Label>
              <div className="flex items-center">
                <DateInput value={startDate} onChange={handleStartDateChange} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label>To</Label>
              <div className="flex items-center">
                <DateInput value={endDate} onChange={handleEndDateChange} />
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <FiltersPopover conversationId={conversationId} userId={userId} level={level}>
              <Button variant="ghost" size="icon">
                <Filter />
              </Button>
            </FiltersPopover>
            <Tooltip>
              <TooltipTrigger>
                <Button variant="ghost" size="icon" onClick={() => refetch()}>
                  <RefreshCw className={cn('transition-transform duration-500', { 'animate-spin': isFetching })} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </PageActionPortal>
      <div className="rounded-lg border border-gray-6 bg-gray-1 text-gray-12">
        {logs.length === 0 && !isFetching ? (
          <EmptyState
            className="h-[calc(100vh-14rem)]"
            title="No logs found."
            description="No logs were found for the selected period. Please try another date range."
            icon={Terminal}
            iconSize={10}
          />
        ) : (
          <ScrollArea className="h-[calc(100vh-14rem)]">
            <InView onChange={(inView) => setIsAtTop(inView)}>
              <div ref={topRef} />
            </InView>
            <div
              className={cx('flex h-16 items-center justify-center', {
                invisible: isFetching && !isFetchingPreviousPage,
              })}
            >
              {isFetchingPreviousPage ? (
                <Spinner size="6" />
              ) : hasPreviousPage ? (
                <Button onClick={() => fetchPreviousPage()} variant="outline" color="gray">
                  Load more
                </Button>
              ) : (
                <Button disabled={true} variant="secondary" color="gray">
                  No more logs for selected period
                </Button>
              )}
            </div>

            <div className="overflow-hidden bg-inherit px-0">
              <div className={cx('relative text-xs [&_*]:font-mono')}>
                {logs.map(({ timestamp, level, message }, index) => (
                  <LogRow
                    key={index}
                    index={index}
                    date={new Date(timestamp)}
                    level={level}
                    message={message}
                    highlight={messageContains}
                  />
                ))}
              </div>
              <InView onChange={(inView) => setIsAtBottom(inView)}>
                <div ref={bottomRef} />
              </InView>
            </div>
            <Button
              variant="secondary"
              size="icon"
              className={cn('absolute left-0 right-0 top-4 mx-auto rounded-full shadow-md hover:bg-gray-4', {
                hidden: isAtTop,
              })}
              onClick={scrollToTop}
            >
              <ChevronUp />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className={cn('absolute bottom-4 left-0 right-0 mx-auto rounded-full shadow-md hover:bg-gray-4', {
                hidden: isAtBottom,
              })}
              onClick={scrollToBottom}
            >
              <ChevronDown />
            </Button>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}

function LogRow({
  date,
  level,
  message,
  highlight,
}: {
  index: number
  date: Date
  message: string
  level: string
  highlight?: string
}): JSX.Element {
  const loglevel = level.toLowerCase()

  const datetime = DateTime.fromJSDate(date)
  const timestamp = datetime.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)

  return (
    <>
      <div
        className={cx('flex gap-2 px-3', {
          'text-gray-11': loglevel === 'debug',
          '!bg-red-3 text-red-12': loglevel === 'error',
          '!bg-amber-3 text-amber-12': loglevel === 'warn',
        })}
      >
        <div className="leading-5 text-gray-10">{timestamp}</div>
        <div className="flex-1 whitespace-pre-wrap leading-5">
          <span className="break-all leading-5 [&>span]:inline-block [&>span]:leading-5">
            {formatMessage(message, highlight)}
          </span>
        </div>
      </div>
    </>
  )
}

function formatMessage(message: string, highlight?: string) {
  let formattedMessage = ''
  try {
    const json = JSON.parse(message)
    formattedMessage = JSON.stringify(json, null, 2)
  } catch {
    formattedMessage = message
  }

  if (!highlight) return message

  const regex = new RegExp(`(${escapeForRegEx(highlight)})`, 'gi') // Case insensitive regex to match the text to highlight
  const parts = formattedMessage.split(regex)

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="rounded !bg-amber-9 px-1">
        {part}
      </mark>
    ) : (
      part
    )
  )
}
